import React, { useState, useEffect } from 'react';
import { LucideLoaderCircle, RefreshCw, Calendar, Clock, ChevronDown, TrendingUp, Droplets, Award, Wallet, Users } from 'lucide-react';
import { useContractServices } from '../../context/ContractServicesContext';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import skyLoader from "../../assets/home-assets/skyLoader.gif";
const GlobalEmissionsHistory = () => {
  const [emissionsData, setEmissionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentEpoch, setCurrentEpoch] = useState(null);
  const [expandedTable, setExpandedTable] = useState(false);
  const [totalEmissionsSum, setTotalEmissionsSum] = useState({ formatted: '0', raw: ethers.BigNumber.from(0) });
  const { services, isReady } = useContractServices();

  // Format number with commas and decimals
  const formatNumber = (value, decimals = 2) => {
    if (!value) return '0';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Format timestamp to date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format duration in days
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const durationSeconds = endTime - startTime;
    const days = Math.floor(durationSeconds / (60 * 60 * 24));
    return `${days} days`;
  };

  // Calculate time remaining in current epoch
  const getTimeRemaining = (endTime) => {
    if (!endTime) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const now = Math.floor(Date.now() / 1000);
    const total = endTime - now;

    if (total <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const days = Math.floor(total / (60 * 60 * 24));
    const hours = Math.floor((total % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((total % (60 * 60)) / 60);
    const seconds = Math.floor(total % 60);

    return { days, hours, minutes, seconds };
  };

  // Format time remaining
  const formatTimeRemaining = (timeObj) => {
    if (timeObj.days === 0 && timeObj.hours === 0 && timeObj.minutes === 0 && timeObj.seconds === 0) {
      return 'Epoch ended';
    }
    return `${timeObj.days}d ${timeObj.hours}h ${timeObj.minutes}m`;
  };

  // Calculate the sum of all emissions across all epochs
  const calculateTotalEmissionsSum = (emissions) => {
    if (!emissions || emissions.length === 0) {
      return { formatted: '0', raw: ethers.BigNumber.from(0) };
    }

    let sum = ethers.BigNumber.from(0);

    emissions.forEach(epoch => {
      if (epoch.totalEmissions && epoch.totalEmissions.value) {
        sum = sum.add(epoch.totalEmissions.value);
      }
    });

    return {
      raw: sum,
      formatted: ethers.utils.formatEther(sum)
    };
  };

  // Fetch global emissions history using the new function
  const fetchEmissionsHistory = async () => {
    if (!isReady || !services?.emissionController) {
      console.log("Emission Controller service not ready");
      return;
    }

    try {
      setLoading(true);

      // Get current epoch info
      const epochInfo = await services.emissionController.getCurrentEpochInfo();
      setCurrentEpoch(epochInfo);

      // Get comprehensive emissions history for all epochs
      const history = await services.emissionController.getGlobalEmissionsHistory();

      // Calculate the sum of all emissions
      const totalSum = calculateTotalEmissionsSum(history);
      setTotalEmissionsSum(totalSum);

      setEmissionsData(history);
      // toast.success("Emissions history loaded successfully");
    } catch (error) {
      console.error("Error fetching emissions history:", error);
      toast.error("Failed to load emissions history");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (isReady && services?.emissionController) {
      fetchEmissionsHistory();
    }
  }, [isReady, services]);

  // Calculate completion percentage of current epoch
  const calculateEpochProgress = () => {
    if (!currentEpoch || !currentEpoch.startTime || !currentEpoch.endTime) return 0;

    const now = Math.floor(Date.now() / 1000);
    const totalDuration = currentEpoch.endTime - currentEpoch.startTime;
    const elapsed = now - currentEpoch.startTime;

    if (elapsed <= 0) return 0;
    if (elapsed >= totalDuration) return 100;

    return Math.floor((elapsed / totalDuration) * 100);
  };

  // Toggle table expansion
  const toggleTableExpansion = () => {
    setExpandedTable(!expandedTable);
  };

  return (
    <div className="pt-10 pb-5">
      <div className="bg-gradient-to-br from-slate-50 via-white to-indigo-50 rounded-xl shadow-lg overflow-hidden border border-indigo-100">
        {/* Header with animated background */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-500 p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-0 left-0 w-20 h-20 bg-white rounded-full blur-3xl transform translate-x-12 -translate-y-6"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl transform -translate-x-12 translate-y-6"></div>
          </div>

          <div className="relative z-10 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center">
                <Droplets className="w-6 h-6 mr-2 text-blue-200" />
                Global Emissions History
              </h2>
              <p className="text-indigo-100 mt-1">Historical CEL token emissions across all epochs</p>
            </div>
            <button
              onClick={fetchEmissionsHistory}
              disabled={loading}
              className="flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 backdrop-blur-sm"
            >
              {loading ? (
                <div className="flex justify-center items-center min-h-screen ">
                  <img
                    src={skyLoader}
                    alt="Loading..."
                    className="w-100 h-100 object-contain"
                  />
                </div>

              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Current Epoch Status - Cards Layout */}
          {currentEpoch && (
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Epoch Number Card */}
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-500 text-white p-6 rounded-xl shadow-md flex flex-col justify-between h-full">
                  <div className="text-white text-sm font-medium uppercase tracking-wider">Current Epoch</div>
                  <div className="mt-2">
                    <div className="text-3xl font-bold">{currentEpoch.currentEpochNumber}</div>
                    <div className="mt-4 flex items-center">
                      <div className="w-full bg-indigo-300/30 rounded-full h-2.5">
                        <div
                          className="bg-white rounded-full h-2.5 transition-all duration-500 ease-in-out"
                          style={{ width: `${calculateEpochProgress()}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs text-indigo-100">{calculateEpochProgress()}%</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-white">
                    {currentEpoch.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 bg-indigo-400/20 rounded-full">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 animate-pulse"></span>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 bg-indigo-400/20 rounded-full">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-1.5"></span>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>

                {/* Time Remaining Card */}
                <div className="bg-white border border-indigo-400 p-6 rounded-xl shadow-sm flex flex-col justify-between h-full">
                  <div className="text-indigo-800 text-sm font-medium uppercase tracking-wider flex items-center">
                    <Clock className="w-4 h-4 mr-1.5 text-indigo-400" />
                    Time Remaining
                  </div>
                  <div className="mt-2">
                    <div className="text-2xl font-bold text-gray-800">
                      {formatTimeRemaining(getTimeRemaining(currentEpoch.endTime))}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      Ends on {formatDate(currentEpoch.endTime)}
                    </div>
                  </div>
                  <div className="mt-2 flex space-x-2">
                    <div className="text-center px-2 py-1 bg-indigo-50 rounded-lg flex-1">
                      <div className="text-lg font-semibold text-indigo-700">
                        {getTimeRemaining(currentEpoch.endTime).days}
                      </div>
                      <div className="text-xs text-indigo-500">Days</div>
                    </div>
                    <div className="text-center px-2 py-1 bg-indigo-50 rounded-lg flex-1">
                      <div className="text-lg font-semibold text-indigo-700">
                        {getTimeRemaining(currentEpoch.endTime).hours}
                      </div>
                      <div className="text-xs text-indigo-500">Hours</div>
                    </div>
                    <div className="text-center px-2 py-1 bg-indigo-50 rounded-lg flex-1">
                      <div className="text-lg font-semibold text-indigo-700">
                        {getTimeRemaining(currentEpoch.endTime).minutes}
                      </div>
                      <div className="text-xs text-indigo-500">Minutes</div>
                    </div>
                  </div>
                </div>

                {/* Start & End Date Card */}
                <div className="bg-white border border-indigo-400 p-6 rounded-xl shadow-sm flex flex-col justify-between h-full">
                  <div className="text-indigo-800 text-sm font-medium uppercase tracking-wider flex items-center">
                    <Calendar className="w-4 h-4 mr-1.5 text-indigo-400" />
                    Epoch Period
                  </div>
                  <div className="mt-2 space-y-3">
                    <div>
                      <div className="text-sm text-gray-500">Start Date</div>
                      <div className="text-base font-semibold text-gray-800">{formatDate(currentEpoch.startTime)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">End Date</div>
                      <div className="text-base font-semibold text-gray-800">{formatDate(currentEpoch.endTime)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-indigo-600 font-medium">
                      {currentEpoch.startTime && currentEpoch.endTime
                        ? formatDuration(currentEpoch.startTime, currentEpoch.endTime)
                        : 'Duration unknown'}
                    </span>
                  </div>
                </div>

                {/* Total Emissions Card - Updated to show sum of all epochs */}
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 rounded-xl shadow-md flex flex-col justify-between h-full">
                  <div className="text-emerald-100 text-sm font-medium uppercase tracking-wider flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1.5" />
                    Total Emissions
                  </div>
                  <div className="mt-2">
                    <div className="text-3xl font-bold">
                      {formatNumber(totalEmissionsSum.formatted)}
                    </div>
                    <div className="text-xl text-emerald-100 mt-1">CEL Tokens</div>
                  </div>
                  <div className="mt-2 text-xs text-emerald-100">
                    Across all {emissionsData.length} epochs
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && !emissionsData.length && (
            <div className="flex justify-center items-center py-16">
              <div className="flex flex-col items-center">
                <LucideLoaderCircle className="animate-spin h-12 w-12 text-indigo-600 mb-4" />
                <span className="text-indigo-600 font-medium">Loading emissions data...</span>
              </div>
            </div>
          )}

          {/* Emissions Table with Toggle */}
          {!loading && emissionsData.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Emissions History</h3>
                <button
                  onClick={toggleTableExpansion}
                  className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  {expandedTable ? 'Show Less' : 'Show All'}
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${expandedTable ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <div className="overflow-hidden rounded-xl border border-indigo-100 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-indigo-100">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-blue-50">
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          Epoch
                        </th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          Total Emissions (CEL)
                        </th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          <div className="flex items-center">
                            <Award className="w-4 h-4 mr-1.5 text-blue-600" />
                            IU Holders
                          </div>
                        </th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          <div className="flex items-center">
                            <Wallet className="w-4 h-4 mr-1.5 text-amber-600" />
                            Treasury
                          </div>
                        </th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1.5 text-purple-600" />
                            Staking
                          </div>
                        </th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-indigo-50">
                      {emissionsData.slice(0, expandedTable ? emissionsData.length : 3).map((epoch, index) => (
                        <tr
                          key={epoch.epochNumber}
                          className={index % 2 === 0 ? 'bg-white hover:bg-indigo-50' : 'bg-indigo-50/30 hover:bg-indigo-50'}
                          style={{ transition: 'background-color 0.2s' }}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">Epoch {epoch.epochNumber}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-emerald-600">{formatNumber(epoch.totalEmissions.formatted)} CEL</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-blue-600">{formatNumber(epoch.iuHolderEmissions.formatted)} CEL</div>
                            <div className="text-xs text-gray-500">{formatNumber(epoch.iuHolderPercentage)}% of total</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-amber-600">{formatNumber(epoch.treasuryEmissions.formatted)} CEL</div>
                            <div className="text-xs text-gray-500">{formatNumber(epoch.treasuryPercentage)}% of total</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-purple-600">{formatNumber(epoch.stakingEmissions.formatted)} CEL</div>
                            <div className="text-xs text-gray-500">{formatNumber(epoch.stakingPercentage)}% of total</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {epoch.epochNumber === currentEpoch?.currentEpochNumber && currentEpoch?.isActive ? (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
                                Completed
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Show pagination indicator if table is collapsed */}
                {!expandedTable && emissionsData.length > 3 && (
                  <div className="bg-indigo-50/50 py-3 text-center">
                    <button
                      onClick={toggleTableExpansion}
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center justify-center mx-auto"
                    >
                      Show {emissionsData.length - 3} more epochs
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* No Data Message */}
          {!loading && emissionsData.length === 0 && (
            <div className="text-center py-12 bg-gradient-to-b from-indigo-50 to-white rounded-lg border border-indigo-100">
              <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center">
                <Droplets className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Emissions Data Available</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Emission data will appear here after the first epoch is processed. Check back later for updates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalEmissionsHistory; 